import React, { createContext, useContext, useMemo, ReactNode } from "react";
import { useTenant } from "@/shared/hooks/useTenant";
import type { TenantConfig } from "@/config/schemas/tenantConfig.schema";

export interface TenantContextType {
  tenant: TenantConfig | null;
  tenantId: string;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const TenantContext = createContext<TenantContextType>({
  tenant: null,
  tenantId: '',
  loading: true,
  error: null,
  refetch: () => {},
});

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const tenantData = useTenant();

  const contextValue = useMemo<TenantContextType>(() => ({
    tenant: tenantData.tenant ?? null,
    tenantId: tenantData.tenantId,
    loading: tenantData.loading,
    error: tenantData.error,
    refetch: tenantData.refetch,
  }), [tenantData.tenant, tenantData.tenantId, tenantData.loading, tenantData.error, tenantData.refetch]);

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenantContext = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error("useTenantContext must be used within a TenantProvider");
  }
  return context;
};
